import dayjs from "dayjs";
import { Body3, getTokenValue, XStack, YStack } from "@medbillai/ui";
import { analyticsService } from "../../../lib/analytics";
import { axisLabels, money, shortDate } from "../format";
import type { ChartPoint, CoveragePlan, Figure, HeadlineFigure } from "../read/readCoverage";
import { metDeductibleLevel } from "../where-you-stand/tiers";
import { StepChart } from "./StepChart";
const CHART_HEIGHT = getTokenValue("$si14", "size");
const DAY_MS = 24 * 60 * 60 * 1000;
export function FigureChart({
  plan,
  headline,
  today,
  history: readings = plan.history,
  bleed,
  height = CHART_HEIGHT,
  limitTop,
  strokeWidth,
  axisGap = "$sp1",
  limitLeft = 0,
  deductibleMetAt = null,
  deductible = null,
  scrub,
  drawInKey
}: {
  plan: CoveragePlan;
  headline: HeadlineFigure;
  today: Date;
  /** The readings to draw, oldest first; the headline's by default. */
  history?: ChartPoint[];
  /** The parent's side padding, run past so the line goes edge to edge. */
  bleed?: "$sp2" | "$sp3";
  height?: number;
  limitTop?: number;
  strokeWidth?: number;
  axisGap?: "$sp0.5" | "$sp1";
  limitLeft?: "$sp1.5" | "$sp2" | 0;
  /** On an out-of-pocket chart, marks where the deductible was met. */
  deductibleMetAt?: Date | null;
  /** The same tier's deductible: once met, an out-of-pocket chart draws its level. */
  deductible?: Figure | null;
  scrub?: boolean;
  drawInKey?: string;
}) {
  const deductibleLevel = headline.kind === "outOfPocket" ? metDeductibleLevel(deductible) : null;
  const history = readingsSoFar(readings, plan.confirmedAt);
  const [first] = history;
  const lastReading = history.at(-1)?.at;
  const end = plan.endedOn ? new Date(Math.max(dayjs(plan.endedOn).valueOf(), lastReading?.getTime() ?? 0)) : plan.ended ? lastReading ?? today : today;
  const yearStart = planYearStart(plan, plan.endedOn ? dayjs(plan.endedOn).toDate() : end);
  const start = history.length > 1 && first && first.at > yearStart ? first.at : yearStart;
  return <YStack gap={axisGap} data-sentry-element="YStack" data-sentry-component="FigureChart" data-sentry-source-file="FigureChart.tsx">
      <YStack marginHorizontal={bleed === "$sp3" ? "$-sp3" : bleed === "$sp2" ? "$-sp2" : 0} data-sentry-element="YStack" data-sentry-source-file="FigureChart.tsx">
        <StepChart points={history} limit={headline.limit ?? 0} start={start} end={end} inset={bleed ? getTokenValue(bleed, "space") : 0} height={height} label={`${money(headline.met)} of ${money(headline.limit ?? 0)} met${deductibleMetAt ? `; deductible met ${shortDate(deductibleMetAt)}` : ""}`} marker={deductibleMetAt ? {
        at: deductibleMetAt,
        label: "Deductible"
      } : null} reference={deductibleLevel === null ? null : {
        amount: deductibleLevel,
        label: "Deductible"
      }} scrub={scrub} onScrubStart={() => analyticsService.trackEvent("CoverageChartScrubbed", {
        figure: headline.kind
      })}
      // Where you stand swaps tier and network in place; drawInKey names the network.
      scrubKey={`${headline.kind}:${headline.tier}:${drawInKey ?? ""}`} drawInKey={drawInKey} limitLabel={headline.kind === "deductible" ? "Deductible" : "Out-of-pocket max"} limitLeft={limitLeft} limitTop={limitTop} strokeWidth={strokeWidth} data-sentry-element="StepChart" data-sentry-source-file="FigureChart.tsx" />
      </YStack>
      <XStack justifyContent="space-between" data-sentry-element="XStack" data-sentry-source-file="FigureChart.tsx">
        {axisLabels(start, end, plan.ended).map(text => <Body3 key={text} color="$textInverseFaint">
            {text}
          </Body3>)}
      </XStack>
    </YStack>;
}

/** One reading, taken by the latest check: there's no year so far to draw. */
export const isFirstCheck = ({
  history,
  confirmedAt
}: Pick<CoveragePlan, "history" | "confirmedAt">) => {
  const [only] = history;
  return history.length === 1 && only !== undefined && confirmedAt !== null && Math.abs(only.at.getTime() - confirmedAt.getTime()) < DAY_MS;
};

/** The API drops readings that didn't move: a lone older reading still holds at the latest check. */
const readingsSoFar = (history: ChartPoint[], confirmedAt: Date | null): ChartPoint[] => {
  const [only] = history;
  return history.length === 1 && only && confirmedAt && !isFirstCheck({
    history,
    confirmedAt
  }) ? [only, {
    at: confirmedAt,
    met: only.met
  }] : history;
};

/** The calendar year of `end` when the payer gave no plan year. */
const planYearStart = ({
  planYear
}: Pick<CoveragePlan, "planYear">, end: Date) => (planYear?.start ? dayjs(planYear.start) : dayjs(end).startOf("year")).toDate();